import { useMemo, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import debounce from 'lodash/debounce';

import { type CustomFieldValue } from '@/api';
import Box from '@/design_system/Box';
import FoldableSection from '@/design_system/FoldableSection';
import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import InputText from '@/design_system/InputText/InputText';
import { Label } from '@/design_system/Label/Label';
import { OptionalLabel } from '@/design_system/Label/OptionalLabel';
import { RadioBox, RadioBoxGroup } from '@/design_system/Radio/RadioBox/RadioBox';
import Stack from '@/design_system/Stack';
import Title from '@/design_system/Typography/Title';
import { useOrganizationWorkflows } from '@/models/organization';
import { useUpdateDraftRequest, useUpdateRequestType } from '@/models/request';
import { getUserStoresWithPermission, hasPermission } from '@/models/user';
import { SelectDueAtType } from '@/routes/Requests/New/components/SelectDueAtType';
import { useCurrentSession } from '@/services/auth';
import { useDatabaseTranslation } from '@/services/i18n';
import { unique } from '@/utils/array';

import { useRequestContext } from '../../contexts/RequestContext';

const RequestInformationForm = () => {
  const { t } = useLingui();
  const { currentSession } = useCurrentSession();
  const { request, view, errors } = useRequestContext();

  const { data: workflows } = useOrganizationWorkflows(request.organization.slug);

  const canCreateClientRequest =
    hasPermission(currentSession, 'create_client_request', {
      organizationId: request.organizationId,
    }) && workflows?.some((w) => w.type === 'client_via_store');

  const canCreateStoreRequest =
    hasPermission(currentSession, 'create_store_request', {
      organizationId: request.organizationId,
    }) && workflows?.some((w) => w.type === 'store');

  const { mutate: updateDraftRequest } = useUpdateDraftRequest(request.id);
  const { mutate: updateRequestType } = useUpdateRequestType(request.id);

  const debouncedUpdateDraftRequest = useMemo(() => {
    return debounce(updateDraftRequest, 500);
  }, [updateDraftRequest]);

  const [externalReference, setExternalReference] = useState(request.externalReference ?? '');
  const [customFieldValues, setCustomFieldValues] = useState<CustomFieldValue[]>(
    view.information.customFields.map((customField) => ({
      id: customField.id,
      value:
        request.customFieldValues?.find(
          (customFieldValue) => customFieldValue.id === customField.id
        )?.value ?? null,
    })) ?? null
  );

  const requestType = request.client ? 'client' : 'store';

  const stores = unique(
    [
      ...getUserStoresWithPermission(
        currentSession,
        'create_client_request',
        request.organizationId
      ),
      ...getUserStoresWithPermission(
        currentSession,
        'create_store_request',
        request.organizationId
      ),
    ],
    'id'
  );

  return (
    <FoldableSection
      label={
        <Title as="h2">
          <Trans id="requests.new.settings.title">Request information</Trans>
        </Title>
      }
    >
      <Box padding="16px" gap="1rem" style={{ flex: 'initial' }}>
        <Stack gap="1rem">
          {canCreateClientRequest && canCreateStoreRequest && (
            <Label label={t({ id: 'requests.new.requestor-type.label', message: 'Type' })}>
              <RadioBoxGroup
                name="request-type"
                value={requestType}
                onChange={(newRequestType) => {
                  if (newRequestType !== requestType) {
                    updateRequestType({ type: newRequestType });
                  }
                }}
                disabled={!request.draft}
              >
                <RadioBox
                  value="client"
                  ariaLabel={t({
                    id: 'requests.new.requestor-type.client',
                    message: 'Client request',
                  })}
                  title={t({ id: 'requests.new.requestor-type.client', message: 'Client request' })}
                >
                  <Trans id="requests.new.requestor-type.client.sub-text">
                    For client-owned items with issues
                  </Trans>
                </RadioBox>
                <RadioBox
                  value="store"
                  ariaLabel={t({
                    id: 'requests.new.requestor-type.store',
                    message: 'Damaged stock request',
                  })}
                  title={t({
                    id: 'requests.new.requestor-type.store',
                    message: 'Damaged stock request',
                  })}
                >
                  <Trans id="requests.new.requestor-type.store.sub-text">
                    For brand-owned items damaged in-store
                  </Trans>
                </RadioBox>
              </RadioBoxGroup>
            </Label>
          )}

          {stores.length > 1 && (
            <InputSelect
              variant="select"
              label={t({ id: 'requests.new.store.label', message: 'Store' })}
              options={stores.map((store) => store)}
              value={stores.find((store) => store.id === request.storeId)}
              getOptionValue={(store) => store.id}
              getOptionLabel={(store) => store.name}
              size="medium"
              onChange={(store) => {
                if (store) {
                  updateDraftRequest({ storeId: store.id });
                }
              }}
              style={{ flex: 1 }}
            />
          )}

          <SelectDueAtType />

          {view.information.externalReference.enabled && (
            <InputText
              label={
                <>
                  <Trans id="requests.new.external-reference.label">Internal reference</Trans>
                  {!view.information.externalReference.required && <OptionalLabel />}
                </>
              }
              placeholder={t({
                id: 'requests.new.external-reference.placeholder',
                message: 'Add a reference',
              })}
              size="medium"
              value={externalReference}
              onChange={(value) => {
                setExternalReference(value);
                debouncedUpdateDraftRequest({ externalReference: value });
              }}
            />
          )}

          {view.information.salesAdvisor.enabled && (
            <InputSelect
              variant="select"
              label={
                <>
                  <Trans id="requests.new.sales-advisor.label">Sales advisor</Trans>
                  {!view.information.salesAdvisor.required && <OptionalLabel />}
                </>
              }
              placeholder={t({
                id: 'requests.new.sales-advisor.placeholder',
                message: 'Select a sales advisor...',
              })}
              isSearchable
              isClearable
              value={request.salesAdvisor ? { value: request.salesAdvisor } : null}
              options={request.store?.storeConfig?.salesAdvisors
                ?.sort()
                .map((value) => ({ value }))}
              getOptionValue={(option) => option.value}
              getOptionLabel={(option) => option.value}
              onChange={(option) => {
                updateDraftRequest({ salesAdvisor: option?.value ?? null });
              }}
              size="medium"
              style={{ flex: 1 }}
              error={
                errors?.information?.missingSalesAdvisor
                  ? t({
                      id: 'requests.new.sales-advisor.error',
                      message: 'Please select a sales advisor',
                    })
                  : undefined
              }
            />
          )}

          <RequestCustomFields
            customFieldValues={customFieldValues}
            onChange={(values) => {
              setCustomFieldValues(values);
              debouncedUpdateDraftRequest({ customFieldValues: values });
            }}
          />
        </Stack>
      </Box>
    </FoldableSection>
  );
};

const RequestCustomFields = ({
  customFieldValues,
  onChange,
}: {
  customFieldValues: CustomFieldValue[];
  onChange: (values: CustomFieldValue[]) => void;
}) => {
  const { t } = useLingui();
  const { _db } = useDatabaseTranslation();
  const { view, errors } = useRequestContext();

  if (!view.information.customFields.length) {
    return null;
  }

  return (
    <>
      {view.information.customFields.map((customField) => (
        <InputText
          key={customField.id}
          label={
            <>
              {customField.label ? _db(customField.label) : undefined}
              {!customField.required && <OptionalLabel />}
            </>
          }
          placeholder={t({
            id: 'requests.new.custom-field.placeholder',
            message: 'Add a value',
          })}
          size="medium"
          value={
            customFieldValues?.find((customFieldValue) => customFieldValue.id === customField.id)
              ?.value ?? undefined
          }
          onChange={(newValue) => {
            const newCustomFieldValues = customFieldValues.map((customFieldValue) =>
              customFieldValue.id === customField.id
                ? { ...customFieldValue, value: newValue.trim() || null }
                : customFieldValue
            );

            onChange(newCustomFieldValues);
          }}
          error={
            errors?.information?.missingCustomFieldValues?.find(
              (customFieldMissingValue) => customFieldMissingValue.id === customField.id
            )?.missingValue
              ? t({
                  id: 'requests.new.custom-field.error',
                  message: 'Please add a value',
                })
              : undefined
          }
        />
      ))}
    </>
  );
};

export default RequestInformationForm;
